const normalizeMailto = (u) => {
  if (!/^mailto:/i.test(u)) return u
  const [base, query] = String(u).split('?')
  if (!query) return u

  const compact = query.replace(/\s*&\s*/g, '&').replace(/\s*=\s*/g, '=')

  const parts = compact.split('&').map((keyValue) => {
    if (!keyValue) return keyValue
    const eq = keyValue.indexOf('=')
    if (eq === -1) {
      try {
        return encodeURIComponent(decodeURIComponent(keyValue))
      } catch {
        return encodeURIComponent(keyValue)
      }
    }
    const key = keyValue.slice(0, eq)
    const val = keyValue.slice(eq + 1)

    let encKey, encVal
    try {
      encKey = encodeURIComponent(decodeURIComponent(key))
    } catch {
      encKey = encodeURIComponent(key)
    }

    const valNormalized = val.replace(/ /g, '%20')
    try {
      encVal = encodeURIComponent(decodeURIComponent(valNormalized))
    } catch {
      encVal = encodeURIComponent(valNormalized)
    }

    return `${encKey}=${encVal}`
  })

  return `${base}?${parts.join('&')}`
}

// Placeholders that can be embedded in URLs (e.g., social sharing links)
const EMBEDDABLE_PLACEHOLDERS = ['newsletter', 'title', 'subject']

// All allowed placeholder codes (without ##)
const ALLOWED_PLACEHOLDERS = [
  'view_online',
  'unsubscribe',
  'update_contact',
  'confirmation_link',
  'gdpr_consent',
  'tell_friend',
  'newsletter',
  'title',
  'subject',
  'firstname',
  'lastname',
  'email',
  'sms_number',
  'list_name',
  'sender_name',
]

// Upper bound on the URL length we'll run the validator regex against. The
// regex has ambiguous overlapping quantifiers in its path/query groups, so a
// pathological input (e.g. thousands of `?`) backtracks quadratically — ~18 ms
// at this cap, multiplying into seconds past ~10k chars. validateUrl runs on
// the hot editing path (per keystroke, and per link in the linkCheck computed),
// so we reject anything longer up front. 2048 is the conventional max URL length
// and far beyond any legitimate link, including long ad-tracker URLs.
const MAX_URL_LENGTH = 2048

// Single source of truth for the http(s)/mailto/tel URL shape used by both
// validation branches below, so they can't drift. The http(s) path and query
// character classes allow the full RFC 3986 sub-delim set — including `$ ' ( )`
// (FE-1158: ad-platform click trackers carry a literal `$` before encoded GDPR
// macros, e.g. `gdpr=$%7bGDPR%7d`; parenthesised paths like Wikipedia's
// `/wiki/Foo_(bar)` are valid too) — plus the nordic letters and pct-encoding we
// accept unescaped. No `g` flag, so sharing one instance with .test() is safe.
const URL_VALIDATION_RE =
  /^(https?:\/\/)(www\.)?([a-zA-Z0-9åäöæøðþÅÄÖÆØÐÞ-]{1,63}\.)+[a-zA-ZåäöæøðþÅÄÖÆØÐÞ]{2,63}(\b(\/[-a-zA-Z0-9åäöæøðþÅÄÖÆØÐÞ@:%_\+.~#?&=,;\/!*$'()]*)?(\?[-a-zA-Z0-9åäöæøðþÅÄÖÆØÐÞ@:%_\+.~#?&=,;\/!*$'()]*)?)?$|^mailto:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9åäöæøðþÅÄÖÆØÐÞ.-]+\.[a-zA-ZåäöæøðþÅÄÖÆØÐÞ]{2,}(?:\?[-a-zA-Z0-9@:%_\+.~#?&=,;\/!*()%\s]*)?$|^tel:\+?[0-9\-().\s]*$/

export const validateUrl = (url) => {
  if (!url) return true

  // Guard the regex against pathological-length input before any work.
  if (String(url).length > MAX_URL_LENGTH) return false

  const ampFixed = String(url).replace(/&amp;/g, '&')

  const normalized = normalizeMailto(ampFixed)

  const sanitizedUrl = String(normalized).toLowerCase()

  // Find all placeholders in the URL
  const placeholderMatches = sanitizedUrl.match(/##([a-zA-Z0-9_]+)##/g) || []

  if (placeholderMatches.length > 0) {
    // Extract placeholder codes (without ##)
    const placeholderCodes = placeholderMatches.map((p) =>
      p.replace(/##/g, '').toLowerCase()
    )

    // Check all placeholders are in the allowed list
    const allAllowed = placeholderCodes.every((code) =>
      ALLOWED_PLACEHOLDERS.includes(code)
    )
    if (!allAllowed) return false

    // Check if any non-embeddable placeholder exists
    const hasNonEmbeddable = placeholderCodes.some(
      (code) => !EMBEDDABLE_PLACEHOLDERS.includes(code)
    )

    if (hasNonEmbeddable) {
      // Non-embeddable placeholders must be exact match only
      const exactPlaceholderRe = /^##[a-zA-Z0-9_]+##$/
      return exactPlaceholderRe.test(sanitizedUrl)
    }

    // Only embeddable placeholders - validate as URL with embedded placeholder
    // Replace placeholders with a valid URL segment to validate URL structure
    const urlForValidation = sanitizedUrl.replace(
      /##[a-zA-Z0-9_]+##/g,
      'placeholder'
    )

    const exactPlaceholderRe = /^##[a-zA-Z0-9_]+##$/

    // Valid if it's an exact placeholder OR a valid URL structure with embedded placeholder
    return (
      exactPlaceholderRe.test(sanitizedUrl) ||
      (URL_VALIDATION_RE.test(urlForValidation) &&
        !/\.{2,}/.test(urlForValidation))
    )
  }

  const validUrl =
    URL_VALIDATION_RE.test(sanitizedUrl) && !/\.{2,}/.test(sanitizedUrl)

  return validUrl
}

// Stricter check for <img src> values: must be http(s). validateUrl also
// accepts mailto:/tel:, which are nonsensical in an image context.
export const isHttpImageUrl = (url) => {
  if (!url) return false
  if (!/^https?:\/\//i.test(String(url))) return false
  return validateUrl(url)
}
